import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Typography,
  useTheme
} from '@mui/material';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useServerCountdown } from '../../hooks/useServerCountdown';
import { serverNow } from '../../utils/serverClock';
import { formatCountdown } from '../../utils/countdownFormat';
import { useQuizRsvp } from '../../hooks/useQuizRsvp';
import { QuizSummary } from '../../types/quiz';
import { isLiveQuizEnded, isLiveQuizRunning } from '../../utils/liveQuizStatus';

const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
/** Matches the backend's late-entrant ranked-replay window (see MissedEventPolicy). */
const REPLAY_RANKED_WINDOW_MS = 48 * 60 * 60 * 1000;
/** The join window is open only during question 1 (index 0); see live-quiz plan. */
const LAST_JOINABLE_QUESTION_INDEX = 0;

type RailColor = 'grey' | 'amber' | 'red';

interface CardPresentation {
  badgeLabel: string;
  badgeFilled: boolean;
  railColor: RailColor;
  showLiveDot: boolean;
  ctaLabel: string;
  ctaEnabled: boolean;
  ctaVariant: 'contained' | 'outlined' | 'text';
  ctaColor: 'primary' | 'error' | 'inherit';
  navigateTo: string | null;
  showRatingPill: boolean;
  showRsvp: boolean;
  secondaryLine: string;
  /** Fed to `useServerCountdown` purely to re-render as this phase ends. */
  countdownTarget: string | null;
}

/** "Thu 4:00 PM" — locale fixed so the string is deterministic in tests; timezone is left to the browser, matching every other date display in the app. */
function formatAbsoluteStart(iso: string): string {
  const date = new Date(iso);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${weekday} ${time}`;
}

/** "3:55 PM" */
function formatAbsoluteTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * Pure CTA/badge/rail state machine over `(now, lobbyOpensAt, startsAt,
 * status, joined, myAttempt, missedEventPolicy)` — see the state table in
 * the plan. Kept separate from rendering so every row is a plain data
 * comparison, not a DOM assertion waiting to happen.
 */
function deriveCardPresentation(nowMs: number, quiz: QuizSummary): CardPresentation {
  const live = quiz.live;
  if (!live) {
    // Callers only render this card for quizMode: 'live' with live info
    // attached; a missing `live` block is a caller bug, not a display state.
    throw new Error('LiveQuizCard requires quiz.live to be present');
  }

  const lobbyOpensAtMs = Date.parse(live.lobbyOpensAt);
  const startsAtMs = Date.parse(live.startsAt);
  const running = isLiveQuizRunning(live);
  const ended = isLiveQuizEnded(live);
  const attemptCompleted = !!quiz.myAttempt?.completedAt;
  const scheduleLine = `${formatAbsoluteStart(live.startsAt)} · lobby opens ${formatAbsoluteTime(live.lobbyOpensAt)}`;

  if (ended) {
    // Row 5 wins over every missed-event policy: a result to view always
    // outranks a replay offer, whatever the policy says.
    if (attemptCompleted) {
      return {
        badgeLabel: 'EVENT ENDED',
        badgeFilled: false,
        railColor: 'grey',
        showLiveDot: false,
        ctaLabel: 'View results',
        ctaEnabled: true,
        ctaVariant: 'outlined',
        ctaColor: 'primary',
        navigateTo: `/student/quizzes/${quiz.id}/results`,
        showRatingPill: false,
        showRsvp: false,
        // The list contract (QuizSummary.myAttempt) only carries id/score/
        // completedAt — no placement or field size — so that's all this
        // line can honestly report.
        secondaryLine: `Your score: ${quiz.myAttempt?.score ?? 0}`,
        countdownTarget: null
      };
    }

    if (live.missedEventPolicy === 'locked_out') {
      return {
        badgeLabel: 'EVENT ENDED',
        badgeFilled: false,
        railColor: 'grey',
        showLiveDot: false,
        ctaLabel: 'Missed',
        ctaEnabled: false,
        ctaVariant: 'text',
        ctaColor: 'inherit',
        navigateTo: null,
        showRatingPill: false,
        showRsvp: false,
        secondaryLine: 'This event was the only chance',
        countdownTarget: null
      };
    }

    if (live.missedEventPolicy === 'replay_ranked') {
      // The rating window is 48h from when the room finished — the quiz
      // list has no age cutoff, so without this a card could promise a
      // rating indefinitely for a replay the server will accept but never
      // rate. A missing finishedAt (the backend field isn't wired everywhere
      // yet) fails toward "window closed" — the same wrong direction as
      // never expiring would defeat the point of checking at all.
      const finishedAtMs = live.finishedAt !== null ? Date.parse(live.finishedAt) : null;
      const withinRankedWindow = finishedAtMs !== null && nowMs - finishedAtMs <= REPLAY_RANKED_WINDOW_MS;

      if (withinRankedWindow) {
        return {
          badgeLabel: 'EVENT ENDED',
          badgeFilled: false,
          railColor: 'grey',
          showLiveDot: false,
          ctaLabel: 'Play now',
          ctaEnabled: true,
          ctaVariant: 'contained',
          ctaColor: 'primary',
          navigateTo: `/student/quizzes/${quiz.id}/play`,
          showRatingPill: true,
          showRsvp: false,
          secondaryLine: 'You missed this one — this attempt still counts',
          countdownTarget: null
        };
      }

      return {
        badgeLabel: 'EVENT ENDED',
        badgeFilled: false,
        railColor: 'grey',
        showLiveDot: false,
        ctaLabel: 'Play unranked',
        ctaEnabled: true,
        ctaVariant: 'outlined',
        ctaColor: 'primary',
        navigateTo: `/student/quizzes/${quiz.id}/play`,
        showRatingPill: false,
        showRsvp: false,
        secondaryLine: 'The ranked window for this quiz has closed — you can still play it for XP',
        countdownTarget: null
      };
    }

    // replay_unranked — the default policy.
    return {
      badgeLabel: 'EVENT ENDED',
      badgeFilled: false,
      railColor: 'grey',
      showLiveDot: false,
      ctaLabel: 'Play unranked',
      ctaEnabled: true,
      ctaVariant: 'outlined',
      ctaColor: 'primary',
      navigateTo: `/student/quizzes/${quiz.id}/play`,
      showRatingPill: false,
      showRsvp: false,
      secondaryLine: 'You missed this one — play it unranked',
      countdownTarget: null
    };
  }

  if (running) {
    // An already-joined student is never blocked here — re-entering (e.g.
    // after a refresh mid-quiz) must always work, whatever question the
    // room is on.
    if (live.joined) {
      return {
        badgeLabel: 'LIVE NOW',
        badgeFilled: true,
        railColor: 'red',
        showLiveDot: false,
        ctaLabel: 'Back to quiz',
        ctaEnabled: true,
        ctaVariant: 'contained',
        ctaColor: 'error',
        navigateTo: `/student/live/${live.roomId}`,
        showRatingPill: true,
        showRsvp: false,
        secondaryLine: `${live.participantCount} playing`,
        countdownTarget: null
      };
    }

    // The join window closes after question 1 — a student who never joined
    // and clicks in later gets a 409 with nothing to show for it. Disable
    // rather than promise a join that can't succeed.
    if (live.currentQuestionIndex > LAST_JOINABLE_QUESTION_INDEX) {
      return {
        badgeLabel: 'LIVE NOW',
        badgeFilled: true,
        railColor: 'red',
        showLiveDot: false,
        ctaLabel: 'Already under way',
        ctaEnabled: false,
        ctaVariant: 'contained',
        ctaColor: 'error',
        navigateTo: null,
        showRatingPill: false,
        showRsvp: false,
        secondaryLine: 'Check back after it ends for the results',
        countdownTarget: null
      };
    }

    return {
      badgeLabel: 'LIVE NOW',
      badgeFilled: true,
      railColor: 'red',
      showLiveDot: true,
      ctaLabel: 'Join now',
      ctaEnabled: true,
      ctaVariant: 'contained',
      ctaColor: 'error',
      navigateTo: `/student/live/${live.roomId}`,
      showRatingPill: true,
      showRsvp: false,
      secondaryLine: `${live.participantCount} playing`,
      countdownTarget: null
    };
  }

  // Neither running nor ended: before, or during, the lobby window.
  if (nowMs < lobbyOpensAtMs) {
    const remainingToLobby = Math.max(0, lobbyOpensAtMs - nowMs);
    // Beyond 12h a relative countdown reads as noise; an absolute time is
    // more useful ("this quiz starts Thursday at 4pm").
    const ctaLabel =
      remainingToLobby > TWELVE_HOURS_MS
        ? `Starts ${formatAbsoluteStart(live.startsAt)}`
        : `Starts in ${formatCountdown(remainingToLobby)}`;

    return {
      badgeLabel: 'LIVE EVENT',
      badgeFilled: false,
      railColor: 'grey',
      showLiveDot: false,
      ctaLabel,
      ctaEnabled: false,
      ctaVariant: 'contained',
      ctaColor: 'primary',
      navigateTo: null,
      showRatingPill: true,
      showRsvp: true,
      secondaryLine: scheduleLine,
      countdownTarget: live.lobbyOpensAt
    };
  }

  if (live.roomId != null) {
    return {
      badgeLabel: 'LOBBY OPEN',
      badgeFilled: true,
      railColor: 'amber',
      showLiveDot: false,
      ctaLabel: 'Enter lobby',
      ctaEnabled: true,
      ctaVariant: 'contained',
      ctaColor: 'primary',
      navigateTo: `/student/live/${live.roomId}`,
      showRatingPill: true,
      showRsvp: true,
      secondaryLine: live.joined ? "You're in" : `${live.participantCount} in the lobby`,
      countdownTarget: null
    };
  }

  // Lobby window open, but the lazy room-creation sweep hasn't run yet —
  // count down to the quiz's actual start instead.
  const remainingToStart = Math.max(0, startsAtMs - nowMs);
  return {
    badgeLabel: 'LIVE EVENT',
    badgeFilled: false,
    railColor: 'grey',
    showLiveDot: false,
    ctaLabel: `Starts in ${formatCountdown(remainingToStart)}`,
    ctaEnabled: false,
    ctaVariant: 'contained',
    ctaColor: 'primary',
    navigateTo: null,
    showRatingPill: true,
    showRsvp: true,
    secondaryLine: scheduleLine,
    countdownTarget: live.startsAt
  };
}

interface LiveQuizCardProps {
  /** Must have `quizMode: 'live'` and a non-null `live` block. */
  quiz: QuizSummary;
}

/**
 * Live-event sibling of `QuizCard` — below the title they share nothing
 * (score-plus-3-way-CTA there, time/roster/policy-plus-7-way-CTA here), so
 * this is a separate component rather than an extension. Visually it copies
 * the `QuizCardV2` recipe (flat elevation, rounded corners, hairline
 * border, 4px top rail) without importing from it, since `pages/v2/**`
 * stays frozen and the rail colour here is a state affordance `QuizCardV2`
 * has no notion of.
 */
const LiveQuizCard: React.FC<LiveQuizCardProps> = ({ quiz }) => {
  const theme = useTheme();
  const live = quiz.live;

  // `now` is read directly from the shared clock (never `Date.now()`) so
  // this card agrees with every other countdown on the page even on a
  // device whose own clock is wrong. `useServerCountdown` below exists
  // only to force a re-render as the clock crosses into the next phase —
  // the displayed text always comes from a fresh `now` on that re-render,
  // not from the hook's own state.
  const now = serverNow();
  const presentation = live ? deriveCardPresentation(now, quiz) : null;
  useServerCountdown(presentation?.countdownTarget ?? null);

  const rsvpMutation = useQuizRsvp(quiz.id);

  if (!live || !presentation) {
    return null;
  }

  const railColorValue =
    presentation.railColor === 'red'
      ? theme.palette.error.main
      : presentation.railColor === 'amber'
        ? theme.palette.warning.main
        : theme.palette.grey[400];

  const badgeSx =
    presentation.badgeFilled && presentation.railColor === 'red'
      ? { bgcolor: theme.palette.error.main, color: theme.palette.error.contrastText, fontWeight: 700 }
      : presentation.badgeFilled && presentation.railColor === 'amber'
        ? { bgcolor: theme.palette.warning.main, color: theme.palette.warning.contrastText, fontWeight: 700 }
        : { color: 'text.secondary', fontWeight: 700 };

  const handleRsvpClick = () => {
    // A double-toggled count from an in-flight click is worse than a
    // button that briefly does nothing.
    if (rsvpMutation.isPending) {
      return;
    }
    rsvpMutation.mutate(!live.rsvped);
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: railColorValue
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label={presentation.badgeLabel}
            size="small"
            variant={presentation.badgeFilled ? 'filled' : 'outlined'}
            icon={presentation.showLiveDot ? <FiberManualRecordIcon sx={{ fontSize: '10px !important' }} /> : undefined}
            sx={badgeSx}
          />
          {presentation.showRatingPill && (
            <Chip
              icon={<MilitaryTechIcon sx={{ fontSize: 16 }} />}
              label="Counts for your rating"
              size="small"
              variant="outlined"
              color="primary"
            />
          )}
        </Box>

        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, lineHeight: 1.3 }}>
          {quiz.title}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {presentation.secondaryLine}
        </Typography>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
        {presentation.showRsvp && (
          <Button
            size="small"
            variant={live.rsvped ? 'outlined' : 'text'}
            startIcon={live.rsvped ? <CheckCircleIcon /> : <NotificationsActiveIcon />}
            onClick={handleRsvpClick}
            disabled={rsvpMutation.isPending}
            sx={{ textTransform: 'none' }}
          >
            {live.rsvped ? `You're in (${live.rsvpCount})` : "I'm in"}
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {presentation.ctaEnabled && presentation.navigateTo ? (
          <Button
            variant={presentation.ctaVariant}
            color={presentation.ctaColor}
            component={Link}
            to={presentation.navigateTo}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {presentation.ctaLabel}
          </Button>
        ) : (
          <Button
            variant={presentation.ctaVariant}
            color={presentation.ctaColor}
            disabled
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {presentation.ctaLabel}
          </Button>
        )}
      </CardActions>

      {rsvpMutation.isError && (
        <Typography variant="caption" color="error" sx={{ px: 2, pb: 1.5 }}>
          Couldn't save that — try again
        </Typography>
      )}
    </Card>
  );
};

export default LiveQuizCard;
